/**
 * What a tool call wrote, and where it landed (RFC 0329 3a).
 *
 * ONE RULE for "this call wrote a file", used by everything that records a
 * write and everything that shows one. The recorders (a driven turn, CLI
 * capture) place each write when it comes back; the session's changed menu
 * lists the writes and reads those records. If the two disagreed about what a
 * write is, the menu would list writes nobody placed.
 *
 * RECOGNISED BY SHAPE, NOT BY A TABLE OF NAMES: tools arrive from MCP servers
 * and apps this file has never heard of, and a fixed list would silently drop
 * every one of them.
 *
 * Pure. Placing needs the repo's checkouts, which only a machine with git can
 * list; the caller lists them and hands them in.
 */

/** Argument names for the file a call is about. */
const PATH_KEYS = [
  'file_path',
  'filePath',
  'path',
  'notebook_path',
  'notebookPath',
  'target_file',
]
/** The text a call PUT INTO a file: a replacement, or a whole body. */
const NEW_KEYS = [
  'new_string',
  'newString',
  'new_str',
  'new_text',
  'newText',
  'replace',
]
const CONTENT_KEYS = ['content', 'contents', 'new_source', 'source']
/** Argument names holding a LIST of replacements (MultiEdit and friends). */
const BATCH_KEYS = ['edits', 'replacements', 'operations', 'changes']
/** A tool whose name claims it writes a file, for the arguments that are only
 *  trustworthy in that company (`content` is a body here and a message there). */
const WRITES = /write|create|overwrite|edit|patch|replace/i

/** A string argument, unwrapped: off the log a string is an ImmutableString. */
function text(value: unknown): string {
  if (typeof value === 'string') return value
  const inner = (value as { val?: unknown } | null)?.val
  return typeof inner === 'string' ? inner : ''
}

function record(args: unknown): Record<string, unknown> | null {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null
  return args as Record<string, unknown>
}

function firstKey(args: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = text(args[key])
    if (value) return value
  }
  return ''
}

function hasBatch(args: Record<string, unknown>): boolean {
  for (const key of BATCH_KEYS) {
    const list = args[key]
    if (!Array.isArray(list)) continue
    for (const entry of list) {
      const inner = record(entry)
      if (inner && firstKey(inner, NEW_KEYS)) return true
    }
  }
  return false
}

/**
 * The file a call wrote, or ''.
 *
 * A path alone is not a write: `Read`, `ls` and `grep` all name a file and
 * change nothing. What makes it a write is the path plus TEXT GOING IN: a
 * replacement, a batch of them, or a whole body from a tool whose name says it
 * writes.
 */
export function writtenPath(name: string, args: unknown): string {
  const a = record(args)
  if (!a) return ''
  const path = firstKey(a, PATH_KEYS)
  if (!path) return ''
  if (firstKey(a, NEW_KEYS) || hasBatch(a)) return path
  if (WRITES.test(name) && firstKey(a, CONTENT_KEYS)) return path
  return ''
}

/**
 * Every file a call wrote, in the order it names them.
 *
 * Most tools write one file and `writtenPath` says which. Some write several in
 * one call and name each inside a list: Codex's `fileChange` item carries
 * `changes: [{ path, kind, diff }]` and no path of its own, so read by
 * `writtenPath` alone every Codex edit was invisible. An entry counts when it
 * has a path and says what went in (a diff, a kind, or replacement text).
 */
export function writtenPaths(name: string, args: unknown): string[] {
  const one = writtenPath(name, args)
  if (one) return [one]
  const a = record(args)
  if (!a) return []
  const out: string[] = []
  for (const key of BATCH_KEYS) {
    const list = a[key]
    if (!Array.isArray(list)) continue
    for (const entry of list) {
      const inner = record(entry)
      if (!inner) continue
      const path = firstKey(inner, PATH_KEYS)
      if (!path || out.includes(path)) continue
      if (
        text(inner.diff) ||
        inner.kind !== undefined ||
        firstKey(inner, NEW_KEYS) ||
        firstKey(inner, CONTENT_KEYS)
      )
        out.push(path)
    }
  }
  return out
}

/** A checkout of the repo, as `git worktree list` names it. */
export interface Checkout {
  /** Absolute, symlinks resolved, no trailing separator. */
  root: string
  /** `feat/x`, not `refs/heads/feat/x`. Absent when HEAD is detached. */
  branch?: string
}

/** Which branch a write was on: recorded on its result, read by the changed menu. */
export interface WrittenOn {
  /** The branch the holding checkout had out when the write came back. */
  branch?: string
  /** The file's path from that checkout's root, `/`-separated. */
  relPath: string
}

/**
 * The checkout holding `path`, and the path inside it; null when none does.
 *
 * By the file's own path, never by the folder the agent was in: an agent in
 * the main checkout that writes into a sibling worktree by full path wrote on
 * the sibling's branch. The longest root wins, so a worktree nested inside
 * another checkout's folder claims its own files. The separator is part of the
 * test, so `/repo-other` is not inside `/repo`.
 */
export function placeWrite(
  path: string,
  checkouts: ReadonlyArray<Checkout>,
): WrittenOn | null {
  let best: Checkout | null = null
  for (const c of checkouts) {
    if (!path.startsWith(`${c.root}/`)) continue
    if (!best || c.root.length > best.root.length) best = c
  }
  if (!best) return null
  const relPath = path.slice(best.root.length + 1)
  if (!relPath) return null
  return best.branch ? { branch: best.branch, relPath } : { relPath }
}

/** The record off a message's metadata, or null when it carries none. */
export function readWrittenOn(metadata: unknown): WrittenOn | null {
  const m = record(metadata)
  if (!m) return null
  const relPath = text(m.relPath)
  if (!relPath) return null
  const branch = text(m.branch)
  return branch ? { branch, relPath } : { relPath }
}

/**
 * The checkouts in `git worktree list --porcelain` output, roots as git
 * printed them. The recorders resolve the roots themselves: this module has no
 * file system to ask.
 */
export function parseWorktreeList(porcelain: string): Checkout[] {
  const out: Checkout[] = []
  for (const block of porcelain.split('\n\n')) {
    let root = ''
    let branch: string | undefined
    for (const line of block.split('\n')) {
      if (line.startsWith('worktree ')) root = line.slice('worktree '.length)
      else if (line.startsWith('branch refs/heads/'))
        branch = line.slice('branch refs/heads/'.length)
    }
    if (root) out.push(branch ? { root, branch } : { root })
  }
  return out
}

/** `path` made absolute against `cwd` and normalised: `.` and `..` resolved. */
function absolute(path: string, cwd: string): string {
  const parts: string[] = []
  for (const part of (path.startsWith('/') ? path : `${cwd}/${path}`).split(
    '/',
  )) {
    if (!part || part === '.') continue
    if (part === '..') parts.pop()
    else parts.push(part)
  }
  return `/${parts.join('/')}`
}

/**
 * The real path, or the real path of the nearest ancestor that exists with
 * the rest appended. A written file may since be gone, and on macOS a tool
 * writes `/var/...` where git says `/private/var/...`. `realpath` is the
 * caller's, since only a machine with the files can answer it.
 */
export async function canonicalPath(
  path: string,
  realpath: (p: string) => Promise<string>,
): Promise<string> {
  let head = path
  const tail: string[] = []
  for (;;) {
    try {
      const real = await realpath(head)
      return tail.length ? `${real}/${tail.reverse().join('/')}` : real
    } catch {
      const cut = head.lastIndexOf('/')
      if (cut <= 0) return path
      tail.push(head.slice(cut + 1))
      head = head.slice(0, cut)
    }
  }
}

/**
 * Which branch a file was written on: `path` (relative to `cwd` if not
 * absolute) placed among `checkouts`, both resolved with `realpath`.
 */
export async function placeWrittenFile(
  path: string,
  cwd: string,
  checkouts: ReadonlyArray<Checkout>,
  realpath: (p: string) => Promise<string>,
): Promise<WrittenOn | null> {
  return placeWrite(
    await canonicalPath(absolute(path, cwd), realpath),
    checkouts,
  )
}

/** The repo's checkouts, roots resolved, from `git worktree list` output. */
export async function resolveCheckouts(
  porcelain: string,
  realpath: (p: string) => Promise<string>,
): Promise<Checkout[]> {
  return Promise.all(
    parseWorktreeList(porcelain).map(async (c) => ({
      ...c,
      root: await canonicalPath(c.root, realpath),
    })),
  )
}
